import { ValidationError } from "sequelize"
import {
    MtDriveFile,
    MtDriveFileCreate,
    MtDriveVersionCreate,
    MtDriveCloudGetFile,
    GetFileByNameOptions,
    UploadFileType,
} from "server/models"

import {
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
} from "const"
import { GET, POST, DELETE, validate } from "client/store/utils"
import {
    AwsGetObject,
    AwsUpload,

    GoogleGetObject,
    GoogleUpload,

    MicrosoftGetObject,
    MicrosoftUpload,

    DeleteDriveVersions,
    LoadDriveFolderByProjectId,

    UserData,
    LangType,
    FileValidation,

    initFileCreate,
    initVersionCreate,
    createFileName,
    parseName,
} from "client/store"
import { OrderType, StorageType, UploadCallback, IMAGE_BASE64_CONTENT_TYPE } from "client/utils"
import { BalloonMessage } from "client/components"

export const LoadAllDriveFiles = (accountId: number) => (
    GET(`/mtDriveFile/all/accountId/${accountId}`)
    .then((files: MtDriveFile[]) => files)
)

export const LoadDriveFiles = (
    accountId: number,
    parentFolderId: number,
    sortColumn: keyof MtDriveFile,
    sortOrder: OrderType,
    offset: number,
    limit: number,
) => (
    // eslint-disable-next-line max-len
    GET(`/mtDriveFile/accountId/${accountId}/folderId/${parentFolderId}/orderBy/${sortColumn}/${sortOrder}/limitBy/${offset}/${limit}`)
    .then((files: MtDriveFile[]) => files)
)

export const LoadDriveFileById = (fileId: number) => (
    GET(`/mtDriveFile/id/${fileId}`)
    .then((file: MtDriveFile) => file || null)
)

export const LoadDriveFileByName = (
    folderId: number,
    name: string,
    copy: number,
) => {
    const data: GetFileByNameOptions = {
        folderId,
        name,
        copy,
    }
    return POST(
        "/mtDriveFile/getByName",
        JSON.stringify(data),
    ).then((file: MtDriveFile) => file || null)
}

export const AddDriveFile = (
    file: MtDriveFileCreate,
    version: MtDriveVersionCreate,
) => {
    const data: UploadFileType = {
        file,
        version,
    }
    return POST(
        "/mtDriveFile/add",
        JSON.stringify(data),
    ).then((res) => {
        const id = Number(res)
        if (id) return id

        BalloonMessage("Error while adding file. Check console")
        // eslint-disable-next-line no-console
        console.log(res)
        return null
    })
}

export const SaveDriveFile = (
    file: MtDriveFile,
    lang: LangType,
) => (
    POST(
        "/mtDriveFile/save",
        JSON.stringify(file),
    ).then((res) => {
        const error = res as ValidationError
        return {
            validation: <FileValidation>validate(error, lang),
        }
    })
)

export const MoveDriveFilesToRecycle = (files: readonly MtDriveFile[]) => {
    const removedFiles: MtDriveFile[] = []
    files.forEach((file) => {
        removedFiles.push({
            ...file,
            prevFolderId: file.folderId,
            folderId: null,
        })
    })
    return POST(
        "/mtDriveFile/moveToRecycle",
        JSON.stringify(removedFiles),
    )
}

export const MoveDriveFile = (file: MtDriveFile) => (
    POST(
        "/mtDriveFile/move",
        JSON.stringify(file),
    )
)

export const GetFile = (file: MtDriveFile, defaultFileUrl: string) => { // download images for PDF reports
    const defaultPromise = ConvertDriveImageBase64(null, null, defaultFileUrl)

    if (file && file.versions.length) {
        const { storageId, type } = file.versions[0]

        let promise: Promise<string>
        const props: MtDriveCloudGetFile = {
            accountId: file.accountId,
            fileId: storageId,
        }

        if (file.storage === STORAGE_AWS) promise = AwsGetObject(storageId)
        else if (file.storage === STORAGE_GOOGLE) promise = GoogleGetObject(props)
        else if (file.storage === STORAGE_MICROSOFT) promise = MicrosoftGetObject(props)

        if (promise) {
            return promise.then((base64) => {
                if (base64) return ConvertDriveImageBase64(base64, type, null)
                return defaultPromise
            })
        }
        return defaultPromise
    }
    return defaultPromise
}

const ConvertDriveImageBase64 = (base64: string, type: string, url: string) => (
    new Promise<IMAGE_BASE64_CONTENT_TYPE>((resolve) => {
        const img = new Image()
        if (base64 && type) {
            const data = `data:${type};base64,${base64}`
            img.src = data
        }
        if (url) {
            img.src = url
        }

        img.onload = () => {
            const canvas = document.createElement("canvas")
            canvas.width = img.width
            canvas.height = img.height

            const context = canvas.getContext("2d")
            context.drawImage(img, 0, 0)
            const contextData = canvas.toDataURL("image/png")

            const result: IMAGE_BASE64_CONTENT_TYPE = {
                height: img.height,
                width: img.width,
                data: contextData,
            }
            resolve(result)
        }
    })
)

interface UploadFileProps {
    file: File
    fileName?: string
    revisionNumber?: string
    storage: StorageType
    userData: UserData
    projectId: number // or projectId or targetFolderId
    targetFolderId: number
    callbackSuccess: (file: MtDriveFile) => void
    callbackError?: () => void
}
export const UploadFile = ({
    file,
    fileName = null,
    revisionNumber = null,
    storage,
    userData,
    projectId,
    targetFolderId,
    callbackSuccess,
    callbackError = null,
}: UploadFileProps) => (
    // if project is opened upload to the root of the project folder
    LoadDriveFolderByProjectId(projectId).then((folder) => {
        const folderId = targetFolderId || (folder ? folder.id : 0) // by default upload to the root of the drive

        const { accountId, userId } = userData || {}
        const { name, counter } = parseName(fileName || file.name)

        const driveFile: MtDriveFileCreate = {
            ...initFileCreate,
            folderId,
            accountId,
            owner: userId,
            name,
            copy: counter,
            storage,
        }

        const driveVersion: MtDriveVersionCreate = {
            ...initVersionCreate,
            accountId,
            owner: userId,
            name: fileName || file.name,
            revision: revisionNumber || "0",
            type: file.type,
            size: file.size,
        }

        const callback: UploadCallback = (storageId, sharedUrl) => {
            if (storageId === null) {
                if (callbackError) callbackError()
                return
            }

            AddDriveFile(
                driveFile,
                {
                    ...driveVersion,
                    storageId,
                    storageSharedUrl: sharedUrl,
                },
            ).then((fileId) => {
                if (fileId) {
                    LoadDriveFileById(fileId).then((newFile) => {
                        if (newFile && newFile.versions.length) callbackSuccess(newFile)
                        else if (callbackError) callbackError()
                    })
                } else if (callbackError) callbackError()
            })
        }

        // AWS S3 upload
        if (storage === STORAGE_AWS) {
            AwsUpload(file, callback)
        }

        // Google Drive upload
        if (storage === STORAGE_GOOGLE) {
            GoogleUpload(file, accountId, callback)
        }

        // MS OneDrive upload
        if (storage === STORAGE_MICROSOFT) {
            MicrosoftUpload(file, accountId, callback)
        }
    })
)

export const DeleteDriveFiles = (files: readonly MtDriveFile[]) => {
    const promiseArray: Promise<any>[] = []
    files.forEach((file) => {
        promiseArray.push(
            DeleteDriveVersions(file.versions, file.storage, file.accountId).then((res) => {
                if (res === null) { // error while removing versions from cloud storage
                    BalloonMessage(`File "${createFileName(file)}" moved to the root of Recycle Bin`)
                    return MoveDriveFilesToRecycle([file])
                }

                return DELETE(`/mtDriveFile/delete/${file.id}`)
                .then((removedRows: number) => removedRows)
            }),
        )
    })

    return Promise.all(promiseArray)
}
